import {
  asSavedSearchesResponse,
  type SavedSearchItem,
} from '@iconik-io/iconik-api/src/schema_patched/saved_search';
import {
  httpDeleteSearchSavedBySearchId,
  httpGetSearchSaved,
  httpPostSearchSaved,
  httpPutSearchSavedBySearchId,
  type SearchHttpGetSearchSavedQuery,
  type SearchSavedSearchSchema,
  type SearchSavedSearchSchemaWithId,
} from '@iconik-io/iconik-api/src/services/search';
import { prepareQueryForMainSearch } from '@iconik-io/shared-lib/src/utils/search/prepare_query_for_main_search';

import { SAVED_SEARCH_PAGE_ROUTE } from '../../constants/routes';
import type { AppDispatch, GetState } from '../../store/types';
import { prepareQueryParameter } from '../../utils/search_utils/search_utils';
import { historyPush } from '../history/actions';
import { getRouteSavedSearchId } from '../pages/search/utils';
import { resetPanelsPristine } from '../panels/pristine/slice';
import { selectEffectiveMetadataViewId } from '../panels/search_filters/metadata/selectors';
import { defaultDocTypes, getSearchRequestBody } from '../search/thunks';
import {
  createSavedSearchSuccess,
  deleteSavedSearchSuccess,
  getSavedSearchesNextPageSuccess,
  getSavedSearchesSuccess,
  updateSavedSearchSuccess,
} from './actions';
import { savedSearchElasticToSavedSearch } from './utils';

export function prepareGroupIdParameter(
  value: string | null | undefined,
): string | undefined {
  return value === null ? 'null' : value;
}

export function getSavedSearchesRequest(
  params: Omit<SearchHttpGetSearchSavedQuery, 'group_id'> & {
    group_id: string | null | undefined;
  },
) {
  const { group_id: groupId, query, ...restParams } = params;
  const sort = query ? 'group_id,asc;name,asc' : 'name,asc';
  return httpGetSearchSaved({
    ...restParams,
    group_id: prepareGroupIdParameter(groupId),
    query: prepareQueryParameter(query),
    sort,
    per_page: 100,
  });
}

export function getSavedSearches(params: {
  query?: string;
  groupId?: string | null;
}) {
  return (dispatch: AppDispatch): Promise<unknown> =>
    dispatch(
      getSavedSearchesRequest({
        query: params.query,
        group_id: params.groupId,
      }),
    ).then(({ body }) =>
      dispatch(
        getSavedSearchesSuccess({
          data: asSavedSearchesResponse(body),
          query: params.query,
          groupId: params.groupId,
        }),
      ),
    );
}

export function getSavedSearchesNextPage(
  params: { groupId?: string | null } = {},
) {
  return (dispatch: AppDispatch, getState: GetState) => {
    const { savedSearchesReducer, savedSearchGroups } = getState();
    const {
      savedSearchesPage: page,
      savedSearchesPages: pages,
      savedSearchesQuery: query,
    } = params.groupId
      ? savedSearchGroups.groupsPaginationState[params.groupId]
      : savedSearchesReducer;

    if (page + 1 <= pages) {
      return dispatch(
        getSavedSearchesRequest({
          query,
          page: page + 1,
          group_id: params.groupId,
        }),
      ).then(({ body }) =>
        dispatch(
          getSavedSearchesNextPageSuccess({
            data: asSavedSearchesResponse(body),
            groupId: params.groupId,
          }),
        ),
      );
    }
    return undefined;
  };
}

export function createSavedSearchFromCurrent(
  savedSearchName: string,
  collectionId?: string,
) {
  return (
    dispatch: AppDispatch,
    getState: GetState,
  ): Promise<SearchSavedSearchSchemaWithId> => {
    const state = getState();
    const { searchFilters } = state.panelsState;

    let { filterValues } = searchFilters;
    if (!filterValues.ancestor_collections && collectionId) {
      filterValues = {
        ...filterValues,

        in_collections: [collectionId],
      };
    }
    const searchBody = getSearchRequestBody(state);
    const body: SearchSavedSearchSchema = {
      criteria: {
        doc_types: defaultDocTypes,
        query: searchBody.query,
        filter: searchBody.filter,
        facets_filters: searchBody.facets_filters,
        metadata_view_id: selectEffectiveMetadataViewId(state),
        search_fields: searchBody.search_fields,
      },
      name: savedSearchName,
    };
    // Required for faster search. The backend will do the aggregation only considering these fields.
    if (body.criteria.facets_filters) {
      body.criteria.facets = body.criteria.facets_filters.map(
        ({ name }) => name,
      );
    }
    return dispatch(
      httpPostSearchSaved(body, { index_immediately: 'true' }),
    ).then((response) => {
      dispatch(createSavedSearchSuccess(response.body));
      return response.body;
    });
  };
}

export function deleteSavedSearch(id: string) {
  return (dispatch: AppDispatch, getState: GetState): Promise<void> =>
    dispatch(httpDeleteSearchSavedBySearchId(id)).then(() => {
      dispatch(deleteSavedSearchSuccess(id));
      const { routeParams } = getState();
      if (
        routeParams.route === SAVED_SEARCH_PAGE_ROUTE &&
        routeParams.params.id === id
      ) {
        dispatch(historyPush('/search'));
      }
    });
}

export function updateSavedSearch(
  id: string,
  data: Partial<SearchSavedSearchSchema>,
) {
  return (dispatch: AppDispatch, getState: GetState): Promise<unknown> => {
    const state = getState();
    const savedSearches = state.savedSearchesReducer.savedSearches;
    const metadataViewId = selectEffectiveMetadataViewId(state);

    let savedSearch: SearchSavedSearchSchema | undefined;

    const currentSavedSearchId = getRouteSavedSearchId(state);

    if (currentSavedSearchId === id) {
      savedSearch =
        state.entities.saved_search_results[id]?.search_criteria_document;
    } else {
      const foundSavedSearch = savedSearches.find((item) => item.id === id);
      if (foundSavedSearch) {
        savedSearch = savedSearchElasticToSavedSearch(foundSavedSearch);
      }
    }
    if (!savedSearch) {
      return Promise.resolve();
    }
    let dataToUpdate = data;
    const query = data.criteria?.query;
    if (query) {
      dataToUpdate = {
        ...dataToUpdate,
        criteria: {
          ...dataToUpdate.criteria,
          query: prepareQueryForMainSearch(query),
        },
      };
    }
    if (dataToUpdate.criteria?.facets_filters) {
      dataToUpdate = {
        ...dataToUpdate,
        criteria: {
          ...dataToUpdate.criteria,
          facets: dataToUpdate.criteria.facets_filters.map(
            (facet) => facet.name,
          ),
        },
      };
    }

    if (metadataViewId) {
      dataToUpdate = {
        ...dataToUpdate,
        criteria: {
          ...dataToUpdate.criteria,
          metadata_view_id: metadataViewId ?? undefined,
        },
      };
    }
    const newSavedSearch = {
      ...savedSearch,
      ...dataToUpdate,
    };
    return dispatch(httpPutSearchSavedBySearchId(id, newSavedSearch)).then(
      () => {
        dispatch(resetPanelsPristine());
        return dispatch(
          // biome-ignore lint/plugin: the store holds the saved search with its criteria object, which the generated schema declares as a string.
          updateSavedSearchSuccess(newSavedSearch as SavedSearchItem),
        );
      },
    );
  };
}

export function updateSavedSearchFromContext(id: string) {
  return (dispatch: AppDispatch, getState: GetState): Promise<unknown> => {
    const state = getState();

    const searchBody = getSearchRequestBody(state);

    const updatedData: Partial<SearchSavedSearchSchema> = {
      criteria: {
        query: searchBody.query,
        filter: searchBody.filter,
        facets_filters: searchBody.facets_filters,
        metadata_view_id: selectEffectiveMetadataViewId(state),
      },
    };
    return dispatch(updateSavedSearch(id, updatedData));
  };
}
